import React from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { getImageUrl } from "../../lib/utils";

const FeaturedCategoriesSection = ({ categories, isLoading, title = "Top Categories" }) => {
  if (isLoading) {
    return (
      <section className="bg-white py-6 md:py-8">
        <div className="w-full md:container mx-auto px-2 sm:px-4">
          <div className="flex items-center justify-between mb-6 md:mb-10">
            <h2 className="text-xl md:text-2xl font-bold text-[#111827] flex items-center gap-2">
              {title} <Sparkles className="w-5 h-5 text-red-500" strokeWidth={2} />
            </h2>
          </div>
        <div className="flex flex-wrap justify-center gap-4 sm:gap-6 md:gap-8">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex flex-col items-center animate-pulse w-[100px] xs:w-[120px] sm:w-[140px] md:w-[160px]">
              <div className="w-full aspect-square bg-slate-100 rounded-2xl mb-4"></div>
              <div className="w-20 h-4 bg-slate-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
    );
  }

  if (!categories || categories.length === 0) return null;

  // Show up to 10 categories, rest are reachable via "View All"
  const displayCategories = categories.slice(0, 10);

  return (
    <section className="bg-white py-4 md:py-6">
      <div className="w-full md:container mx-auto px-2 sm:px-4">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-[#111827] flex items-center gap-2">
            {title} <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-red-500" strokeWidth={2} />
          </h2>
          <Link
            to="/categories"
            className="text-sm md:text-base font-bold text-[#1E3A8A] hover:text-blue-800 flex items-center transition-colors group"
          >
            View All <span className="ml-1 transition-transform group-hover:translate-x-1">›</span>
          </Link>
        </div>

        {/* Categories row - horizontal scroll on mobile/tablet, single grid row from lg up.
            Same pattern as the other product/category rows, so this is always one row. */}
        <div className="flex overflow-x-auto lg:grid lg:grid-cols-10 gap-2 snap-x snap-mandatory pb-2 -mx-2 px-2 lg:mx-0 lg:px-0 lg:overflow-visible scrollbar-hide">
          {displayCategories.map((category) => (
            <Link
              key={category._id || category.id}
              to={`/category/${category.slug}`}
              className="flex flex-col items-center group w-[30%] xs:w-[23%] sm:w-[19%] lg:w-auto flex-shrink-0 snap-start"
            >
              {/* Image Container */}
              <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center mb-2 rounded-xl border border-slate-100 shadow-sm transition-all duration-300 group-hover:shadow-md group-hover:border-blue-200 bg-white overflow-hidden transform-gpu group-hover:-translate-y-1">
                {category.image ? (
                  <img
                    src={getImageUrl(category.image)}
                    alt={category.name}
                    className="w-full h-full object-contain p-1.5 transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full bg-slate-50 flex items-center justify-center">
                    <span className="text-2xl font-bold text-slate-300">
                      {category.name?.charAt(0)}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Title */}
              <span className="text-xs sm:text-sm font-semibold text-slate-700 transition-colors duration-300 group-hover:text-blue-600 text-center leading-snug w-full px-1">
                {category.name}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedCategoriesSection;
